import { jsx, jsxs } from "react/jsx-runtime";
import "./process_polyfill";
import React from "react";
import { Provider } from "react-redux";
import axios from "axios";
import { Button } from "@blueprintjs/core";
import store from "./store";
import { createRoot } from "react-dom/client";
import StandaloneDemo from "./StandaloneDemo";
import FileExplorer from "./FileExplorer.jsx";
import CompanySwitcher from "./CompanySwitcher";
import LoginPage from "./LoginPage";
import "./style.css";
import { DemoPage } from "@teselagen/shared-demo";
const token = localStorage.getItem("ove_token");
if (token) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}
const companyId = localStorage.getItem("ove_company_id");
if (companyId) {
  axios.defaults.headers.common["X-Company-Id"] = companyId;
}
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (!error.config.url.includes("/api/login")) {
        localStorage.removeItem("ove_token");
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);
const originalConsoleError = console.error;
const warningsToSquash = [
  "ReactDOM.render is no longer supported in React 18",
  "unmountComponentAtNode is deprecated"
];
console.error = (...args) => {
  if (typeof args[0] === "string" && warningsToSquash.some((w) => args[0].includes(w))) {
    return;
  }
  originalConsoleError(...args);
};
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    const search = new URLSearchParams(window.location.search).get("reload");
    let isBlocked = search === "false" || localStorage.getItem("suppressReload") === "true";
    if (!isBlocked && window.location.hash.includes("?")) {
      isBlocked = new URLSearchParams(window.location.hash.split("?")[1]).get(
        "reload"
      ) === "false";
    }
    if (!isBlocked) {
      console.log("[HMR] Update accepted, forcing full page reload...");
      window.location.reload();
    } else {
      console.warn(
        "[HMR] Update accepted but browser reload suppressed (reload=false or localStorage)"
      );
    }
  });
  import.meta.hot.data.initialized = true;
}
const demos = {
  Standalone: {
    noDemoMargin: true,
    demo: StandaloneDemo
  }
};
const Demo = () => {
  const [authenticated, setAuthenticated] = React.useState(!!localStorage.getItem("ove_token"));
  const handleNextParam = React.useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const next = urlParams.get("next");
    if (next) {
      if (next.startsWith("#")) {
        window.location.hash = next;
      } else if (next.startsWith("/")) {
        window.location.hash = `#${next}`;
      } else {
        window.location.hash = `#/${next}`;
      }
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);
  React.useEffect(() => {
    if (authenticated) {
      handleNextParam();
    }
  }, [authenticated, handleNextParam]);
  const onLogin = () => {
    setAuthenticated(true);
  };
  if (!authenticated) {
    return /* @__PURE__ */ jsx(LoginPage, { onLogin });
  }
  const handleLogout = () => {
    localStorage.removeItem("ove_token");
    localStorage.removeItem("ove_company_id");
    localStorage.removeItem("ove_user_role");
    localStorage.removeItem("ove_user_email");
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["X-Company-Id"];
    window.location.reload();
  };
  return /* @__PURE__ */ jsx(Provider, { store, children: /* @__PURE__ */ jsx(
    DemoPage,
    {
      demos,
      SideBarComponent: FileExplorer,
      rightHeaderButtons: /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center" }, children: [
        /* @__PURE__ */ jsx(CompanySwitcher, {}),
        /* @__PURE__ */ jsx(
          Button,
          {
            minimal: true,
            icon: "log-out",
            text: "Logout",
            onClick: handleLogout
          }
        )
      ] })
    }
  ) });
};
const domNode = document.querySelector("#demo");
let root;
let isFreshRoot = false;
if (import.meta.hot && import.meta.hot.data.root) {
  root = import.meta.hot.data.root;
} else {
  root = createRoot(domNode);
  isFreshRoot = true;
  if (import.meta.hot) import.meta.hot.data.root = root;
}
const checkReloadParam = () => {
  const search = new URLSearchParams(window.location.search).get("reload");
  if (search === "false" || localStorage.getItem("suppressReload") === "true")
    return true;
  const hash = window.location.hash;
  if (hash.includes("?")) {
    return new URLSearchParams(hash.split("?")[1]).get("reload") === "false";
  }
  return false;
};
if (window.myGlobalDialogKeyListener) {
  window.removeEventListener("keydown", window.myGlobalDialogKeyListener, true);
}
window.myGlobalDialogKeyListener = function(e) {
  if (e.key === "Enter" || e.key === "Escape") {
    const openDialog = document.querySelector(".bp3-overlay-open .bp3-dialog") || document.querySelector(".bp3-dialog");
    if (!openDialog) {
      return;
    }
    if (e.key === "Enter") {
      const activeTag = document.activeElement?.tagName.toLowerCase();
      if (activeTag === "textarea" || activeTag === "input") {
        return;
      }
      const confirmBtn = openDialog.querySelector(".bp3-button.bp3-intent-danger") || openDialog.querySelector(".bp3-button.bp3-intent-primary") || openDialog.querySelector(".bp3-button.bp3-intent-success");
      if (confirmBtn) {
        confirmBtn.click();
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }
    const closeX = openDialog.querySelector(".bp3-dialog-close-button");
    if (closeX) {
      closeX.click();
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const closeBtn = Array.from(openDialog.querySelectorAll(".bp3-button")).find((b) => {
      const text = b.textContent?.trim().toLowerCase();
      return text === "close" || text === "cancel";
    });
    if (closeBtn) {
      closeBtn.click();
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  }
};
window.addEventListener("keydown", window.myGlobalDialogKeyListener, true);
if (import.meta.env.PROD) {
  console.log(
    "%c PRODUCTION MODE ",
    "background: #d9534f; color: white; font-size: 20px; font-weight: bold; border: 2px solid #555; padding: 4px; border-radius: 4px;"
  );
  console.log("Serving pre-built assets from demo-dist. Changes in packages/ove/demo/src will NOT be reflected.");
}
if (isFreshRoot || !checkReloadParam()) {
  root.render(/* @__PURE__ */ jsx(Demo, {}));
} else {
  console.warn("[HMR] Skipping root.render() because reload=false");
}
